import { computed, inject, Injectable, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { OrganisationService } from '@principle-theorem/ng-principle-shared';
import { AccClaimFundingRequest } from '@principle-theorem/principle-core';
import {
  AccApiAction,
  AnyAccRequest,
  FundingRequestStatus,
  IAccClaimFundingRequest,
  IAccDocumentMetadata,
  IAccApiRequest,
  IAccInvoice,
  IAccInvoiceFundingRequest,
  IAccSendDocumentRequest,
  IAccSendDocumentResponse,
  IAccSubmitClaimRequest,
  IAccSubmitClaimResponse,
  IAccSubmitInvoiceRequest,
  IAccSubmitInvoiceResponse,
  IAccSyncRequestsRequest,
  IPatient,
} from '@principle-theorem/principle-core/interfaces';
import {
  DocumentReference,
  filterUndefined,
  Firestore,
  getError,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
  WithRef,
} from '@principle-theorem/shared';

const ACC_API = 'http-acc-api';

@Injectable()
export class AccApiService {
  private _org = inject(OrganisationService);
  private _snackBar = inject(MatSnackBar);

  practice = toSignal(this._org.practice$.pipe(filterUndefined()));
  testModeEnabled = computed(() => !!this.practice()?.accSettings?.testMode);
  isLodging = signal(false);

  async submitClaim(
    claim: WithRef<IAccClaimFundingRequest>
  ): Promise<DocumentReference<AccClaimFundingRequest> | undefined> {
    const practice = this.practice();
    if (!practice) {
      return;
    }

    const testMode = this.testModeEnabled();

    const request: IAccSubmitClaimRequest = {
      action: AccApiAction.SubmitClaim,
      data: {
        claim: claim.data.claimData,
        testMode,
        claimRequestRef: claim.ref,
      },
    };

    this.isLodging.set(true);
    const response = await this._sendRequest<IAccSubmitClaimResponse>(request);
    this.isLodging.set(false);

    if (!response) {
      // eslint-disable-next-line no-console
      console.error('No response from ACC submit claim');
      return;
    }

    const updatedClaim: WithRef<IAccClaimFundingRequest> = {
      ...claim,
      data: {
        ...claim.data,
        claimData: {
          ...claim.data.claimData,
          claimNumber: response.claim.claimNumber,
        },
        testMode,
      },
      status: FundingRequestStatus.Submitted,
      errorMessage: undefined,
    };
    return Firestore.saveDoc(updatedClaim);
  }

  async submitInvoice(
    invoice: IAccInvoice,
    invoiceRequestRef: DocumentReference<IAccInvoiceFundingRequest>
  ): Promise<void> {
    const request: IAccSubmitInvoiceRequest = {
      action: AccApiAction.SubmitInvoice,
      data: {
        invoice,
        invoiceRequestRef,
        testMode: this.testModeEnabled(),
      },
    };
    await this._sendRequest<IAccSubmitInvoiceResponse>(request);
  }

  async syncRequests(patientRef?: DocumentReference<IPatient>): Promise<void> {
    try {
      const practice = this.practice();
      if (!practice) {
        return;
      }
      this._snackBar.open('ACC sync in progress. Updates will appear shortly.');

      const request: IAccSyncRequestsRequest = {
        action: AccApiAction.SyncRequests,
        data: { patientRef, testMode: this.testModeEnabled() },
      };

      await this._sendRequest(request);
    } catch (error) {
      this._snackBar.open(getError(error));
    }
  }

  async sendDocument(
    mediaPaths: string[],
    metadata: IAccDocumentMetadata,
    claimRequestRef: DocumentReference<IAccClaimFundingRequest>
  ): Promise<IAccSendDocumentResponse | undefined> {
    const request: IAccSendDocumentRequest = {
      action: AccApiAction.SendDocument,
      data: {
        mediaPaths,
        metadata,
        claimRequestRef,
        testMode: this.testModeEnabled(),
      },
    };

    return this._sendRequest<IAccSendDocumentResponse>(request);
  }

  private async _sendRequest<T>(
    request: AnyAccRequest
  ): Promise<T | undefined> {
    try {
      const practice = this.practice();
      if (!practice) {
        return;
      }

      return await snapshot(
        httpsCallable<SerialisedData<IAccApiRequest>, T | undefined>(ACC_API)(
          serialise({ practice: practice.ref, request })
        )
      );
    } catch (error) {
      this._snackBar.open(getError(error));
    }
  }
}
